// src/components/demo/DemoRail.jsx — the guided rail: a slim bar above the work
// with the scene the visitor is on, one sentence to read the screen for, and
// Previous / Next. Renders nothing outside demo mode. The visitor can leave the
// rail at any time (free browsing stays allowed, read-only); the bar then offers
// the way back rather than a scene.
//
// Design system (North Star): the rail is neutral paper and hairline like the
// rest of the shell; Janet's purple appears only on the button that asks her.
// The fictional-company disclosure is always in view.
import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react'
import { useDemo } from '../../contexts/DemoContext'
import { useJanetPage } from '../../contexts/JanetPageContext'
import { DEMO_COMPANY } from '../../lib/demoBrand'
import { recordDemoEvent } from '../../lib/demoClient'

export default function DemoRail() {
  const { isDemo, scenes, sceneIndex, scene, next, prev, goTo } = useDemo()
  const { openJanet } = useJanetPage()
  const location = useLocation()
  // Phones: the explanation sits on its own row, two lines until tapped.
  const [open, setOpen] = useState(false)
  useEffect(() => { setOpen(false) }, [sceneIndex])
  if (!isDemo || location.pathname === '/demo/journey') return null

  const onRail = sceneIndex >= 0
  const last = sceneIndex >= scenes.length - 1
  const askJanet = () => {
    recordDemoEvent('janet_ask', scene?.key, { suggested: true })
    // Parked for a panel that mounts on open; the live event reaches one
    // already mounted (the /janet/chat page). The panel consumes whichever
    // arrives first and clears the other.
    try { sessionStorage.setItem('chatrone.janet.pendingAsk', scene.janet) } catch { /* private mode */ }
    openJanet()
    try { window.dispatchEvent(new CustomEvent('janet:ask', { detail: { question: scene.janet } })) } catch { /* no window */ }
  }

  // One row on a laptop. On a phone (below sm) the scene name and Previous /
  // Next keep the first row — Next must never be pushed off the screen — and
  // the explanation and the suggested question drop to a second row.
  const nav = onRail && (
    <div className="flex items-center gap-1 ml-auto shrink-0">
      <button onClick={prev} disabled={sceneIndex === 0} className="w-8 h-8 sm:w-7 sm:h-7 rounded-full border border-[color:var(--ns-hair)] bg-[color:var(--ns-raise)] text-[color:var(--ns-ink)] hover:bg-[color:var(--ns-hair-soft)] disabled:opacity-40 flex items-center justify-center" title="Previous scene" aria-label="Previous scene"><ChevronLeft size={14} /></button>
      <button onClick={next} className="h-8 sm:h-7 px-3 rounded-full bg-[color:var(--ns-ink)] hover:opacity-90 text-[color:var(--ns-paper)] text-xs font-semibold flex items-center gap-1 whitespace-nowrap">
        {last ? 'Finish' : 'Next'} {!last && <ChevronRight size={14} />}
      </button>
    </div>
  )
  const ask = (extra = '') => scene?.janet && (
    <button onClick={askJanet} className={`text-xs font-medium px-2.5 py-1 rounded-full bg-[color:var(--ns-raise)] border border-[color:var(--ns-janet)] text-[color:var(--ns-janet)] hover:bg-[color:var(--ns-hair-soft)] truncate ${extra}`}>
      Ask: “{scene.janet}”
    </button>
  )

  return (
    <div className="shrink-0 border-b border-[color:var(--ns-hair)] bg-[color:var(--ns-paper)] text-[color:var(--ns-ink)]">
      {/* The guided rail below IS the Eighty Days lifecycle now, read through
          real canonical records. This link is deliberately secondary: the
          standalone fixture page compares all five formats side by side in
          one place (useful, but not a second "real" demo). Tidewater, the
          first scenario built, is archived and linked from nowhere. */}
      <div className="px-3 sm:px-4 pt-2 flex flex-wrap items-center gap-3">
        <Link to="/demo/journey" className="inline-flex items-center min-h-11 text-xs underline">All five Eighty Days formats, side by side</Link>
      </div>
      <div className="flex items-center gap-3 px-3 sm:px-4 py-2 min-h-[44px]">
        <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.16em] uppercase text-[color:var(--ns-gray)] shrink-0">
          <Sparkles size={12} /> Demo · {DEMO_COMPANY} · fictional
        </span>
        {onRail ? (
          <>
            <span className="text-xs font-semibold text-[color:var(--ns-ink)] [font-family:var(--ns-font-serif)] truncate sm:whitespace-nowrap min-w-0 sm:shrink-0">
              {sceneIndex + 1}/{scenes.length} · {scene.title}
            </span>
            <span className="hidden sm:block text-xs text-[color:var(--ns-ink-2)] truncate min-w-0 flex-1" title={scene.notice}>{scene.notice}</span>
            {ask('hidden sm:block whitespace-nowrap max-w-[40%] shrink-0')}
            {nav}
          </>
        ) : (
          <>
            <span className="text-xs text-[color:var(--ns-ink-2)] flex-1 min-w-0 truncate">You have left the guided tour. Browse freely — the demo is read-only.</span>
            <button onClick={() => goTo(0)} className="h-8 sm:h-7 px-3 rounded-full bg-[color:var(--ns-ink)] hover:opacity-90 text-[color:var(--ns-paper)] text-xs font-semibold whitespace-nowrap shrink-0">Back to the tour</button>
          </>
        )}
      </div>
      {onRail && (
        <div className="sm:hidden px-3 pb-2 -mt-1 space-y-1.5">
          <button onClick={() => setOpen(o => !o)} className={`w-full text-left text-[11.5px] leading-snug text-[color:var(--ns-ink-2)] ${open ? 'block' : 'line-clamp-2'}`} aria-expanded={open}>
            {scene.notice}
          </button>
          {ask('block max-w-full')}
        </div>
      )}
    </div>
  )
}
